import React, { useEffect, useRef, useState } from 'react';

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

const loadTurnstile = () => new Promise((resolve, reject) => {
  if (window.turnstile) {
    resolve(window.turnstile);
    return;
  }

  const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID);
  if (existingScript) {
    existingScript.addEventListener('load', () => resolve(window.turnstile), { once: true });
    existingScript.addEventListener('error', () => reject(new Error('Could not load Cloudflare Turnstile.')), { once: true });
    return;
  }

  const script = document.createElement('script');
  script.id = TURNSTILE_SCRIPT_ID;
  script.src = TURNSTILE_SCRIPT_URL;
  script.async = true;
  script.defer = true;
  script.onload = () => resolve(window.turnstile);
  script.onerror = () => reject(new Error('Could not load Cloudflare Turnstile.'));
  document.head.appendChild(script);
});

const TurnstileWidget = ({
  siteKey,
  onToken,
  onError,
  resetSignal,
  theme = 'light',
}) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (!siteKey) {
      const error = 'Spam protection is not configured yet. Please try again later.';
      setLoadError(error);
      onError?.(error);
      return undefined;
    }

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !turnstile || !containerRef.current) return;
        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: siteKey,
          theme,
          callback: (token) => {
            setLoadError(null);
            onToken?.(token);
          },
          'expired-callback': () => {
            onToken?.(null);
          },
          'error-callback': () => {
            const error = 'Spam protection could not be verified. Please refresh the challenge and try again.';
            setLoadError(error);
            onToken?.(null);
            onError?.(error);
          },
        });
      })
      .catch((error) => {
        if (cancelled) return;
        const message = error?.message || 'Spam protection could not be loaded.';
        setLoadError(message);
        onError?.(message);
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [siteKey, theme, onError, onToken]);

  useEffect(() => {
    if (widgetIdRef.current !== null && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
      onToken?.(null);
    }
  }, [resetSignal, onToken]);

  return (
    <div className="space-y-2" aria-live="polite">
      <div ref={containerRef} />
      {loadError && (
        <p className="text-xs text-red-600">
          {loadError}
        </p>
      )}
    </div>
  );
};

export default TurnstileWidget;